import { useState } from "react";
import { Link } from "@/lib/router-compat";
import {
  ArrowUpRight,
  X,
  Sparkles,
  MapPin,
  Building2,
  CheckCircle2,
  ArrowRight,
  Download,
} from "lucide-react";
import ResponsiveImage from "@/components/ResponsiveImage";
import { IMG } from "@/lib/optimized-images";
import {
  FEATURED_PROJECT,
  CATEGORY_PROJECTS,
  CLIENT_LOGOS,
  type FeaturedProject,
} from "@/data/projectsSectionData";

type Project = FeaturedProject;

// Measured rendered widths for the 3-up project grid cards.
const PROJECT_SIZES =
  "(min-width: 1280px) 427px, (min-width: 1024px) 302px, (min-width: 768px) 342px, 362px";

// Map project id -> optimized rendition
const PROJECT_RENDITIONS: Record<string, typeof IMG.maxmara> = {
  maxmara: IMG.maxmara,
  "malabar-gold": IMG.malabar,
  "al-salam-bank": IMG.alsalam,
};

export default function ProjectsSection() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const filteredProjects = CATEGORY_PROJECTS;

  return (
    <section
      className="pt-24 pb-0 bg-[#0a0f1d] text-white relative overflow-hidden"
      id="projects"
    >
      {/* Background Accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#002b5c]/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#ffcc29]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-[1400px] mx-auto px-6 relative z-10 pb-16">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-8 border-b border-white/10 pb-8">
          <div>
            <span className="inline-flex items-center gap-2 text-[#ffcc29] font-heading font-bold text-base md:text-lg lg:text-xl tracking-[0.25em] uppercase mb-4">
              <Sparkles className="w-4 h-4" />
              OUR PORTFOLIO
            </span>
            <h2 className="text-white font-heading text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mb-4">
              You've Already Walked Through Our Work
            </h2>
            <p className="text-white/70 text-lg font-body max-w-2xl">
              180+ international brands across Bahrain's most prestigious malls
              - and beyond.
            </p>
          </div>

          <div className="flex flex-col items-start lg:items-end gap-4">
            <Link
              to="/projects"
              className="px-6 py-3 bg-[#ffcc29] text-[#002b5c] font-heading font-bold text-sm tracking-wider uppercase hover:bg-white transition-colors duration-300 inline-flex items-center gap-2"
            >
              View All Projects <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://assets.cdn.filesafe.space/TkZSxIjkCDLzrMPXgP9Q/media/6a7843e188808720191c0cf6.pdf"
              download="Abacus-Project-Portfolio.pdf"
              aria-label="Download the Abacus project portfolio (PDF, 12 MB)"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 border border-[#ffcc29] text-[#ffcc29] font-heading font-bold text-sm tracking-wider uppercase hover:bg-[#ffcc29] hover:text-[#002b5c] transition-colors duration-300 inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download the Abacus project portfolio (PDF, 12 MB)
            </a>
          </div>
        </div>

        {/* Featured Project Banner Card */}
        <div className="mb-16 bg-[#002b5c]/40 border border-white/10 rounded-xs overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-0 group">
          <div className="lg:col-span-7 relative min-h-[350px] lg:min-h-[450px] overflow-hidden">
            <ResponsiveImage
              rendition={IMG.illyFeatured}
              alt={FEATURED_PROJECT.title}
              sizes="(min-width: 1024px) 58vw, 100vw"
              width={800}
              height={500}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute top-4 left-4 bg-[#ffcc29] text-[#002b5c] font-heading font-bold text-sm uppercase px-3 py-1.5 tracking-wider">
              FEATURED CASE STUDY - {FEATURED_PROJECT.category}
            </div>
          </div>

          <div className="lg:col-span-5 p-8 sm:p-12 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-[#ffcc29] text-base font-body mb-3">
                <MapPin className="w-5 h-5" />
                <span>{FEATURED_PROJECT.location}</span>
              </div>
              <h3 className="text-white font-heading text-3xl sm:text-4xl font-bold uppercase tracking-wide mb-4">
                {FEATURED_PROJECT.title}
              </h3>
              <p className="text-white/80 font-body text-base leading-relaxed mb-6">
                {FEATURED_PROJECT.description}
              </p>

              <div className="space-y-2 mb-8">
                {FEATURED_PROJECT.highlights.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 text-base text-white/90 font-body"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#ffcc29] shrink-0 mt-0.5" />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </div>

            <Link
              to="/projects/illy-cafe-city-centre"
              className="inline-flex items-center gap-2 text-[#ffcc29] hover:text-white font-heading font-bold text-sm uppercase tracking-wider transition-colors pt-4 border-t border-white/10"
            >
              Explore Project →
            </Link>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => {
            const targetSlug =
              project.id === "maxmara"
                ? "maxmara-boutique-marassi-galleria"
                : project.id === "malabar-gold"
                  ? "malabar-gold-diamonds-showroom"
                  : "al-salam-bank-al-raya-mall-branch";

            return (
              <Link
                key={project.id}
                to={`/projects/${targetSlug}`}
                className="group relative bg-[#002b5c]/30 border border-white/10 rounded-xs overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:border-[#ffcc29]/50 flex flex-col"
              >
                {/* Card Image */}
                <div className="relative h-64 overflow-hidden bg-black/40">
                  <ResponsiveImage
                    rendition={PROJECT_RENDITIONS[project.id] ?? IMG.maxmara}
                    alt={project.title}
                    sizes={PROJECT_SIZES}
                    width={400}
                    height={256}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1d] via-transparent to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />

                  {/* Position & Category Badge */}
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="bg-[#ffcc29] text-[#002b5c] px-2.5 py-1 text-sm font-heading font-bold">
                      {project.position}
                    </span>
                    <span className="bg-[#002b5c]/90 border border-white/10 backdrop-blur-md text-white/90 px-3 py-1 text-sm font-heading font-bold uppercase tracking-wider">
                      {project.category}
                    </span>
                  </div>

                  {/* Arrow Action Icon */}
                  <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/50 border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 group-hover:bg-[#ffcc29] group-hover:text-[#002b5c] transition-all duration-300">
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-white/60 text-sm font-body mb-2">
                      <MapPin className="w-4 h-4 text-[#ffcc29]" />
                      <span>{project.location}</span>
                    </div>
                    <h3 className="text-white font-heading text-xl font-bold uppercase tracking-wide group-hover:text-[#ffcc29] transition-colors mb-3">
                      {project.title}
                    </h3>
                    <p className="text-white/70 text-sm font-body line-clamp-2 leading-relaxed mb-6">
                      {project.description}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-white/10 flex items-center justify-between text-sm text-white/70 font-body">
                    <span>
                      Scope:{" "}
                      <strong className="text-white font-semibold">
                        {project.area}
                      </strong>
                    </span>
                    <span className="text-[#ffcc29] font-bold text-sm uppercase tracking-wider group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                      Explore →
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Client Logo Carousel - 100% Full Width White Section */}
      <div className="w-full bg-white text-[#002b5c] py-10 sm:py-12 relative z-10">
        <div className="text-center mb-6 sm:mb-10 px-4">
          <span className="text-[#002b5c] font-heading font-bold text-base md:text-lg lg:text-xl tracking-[0.2em] uppercase block mb-1 sm:mb-2">
            CLIENT TRUST
          </span>
          <h3 className="text-[#002b5c] font-heading text-xl sm:text-2xl md:text-3xl font-bold uppercase tracking-wider">
            Trusted by 180+ International Brands
          </h3>
        </div>

        <div className="relative w-full overflow-hidden py-2 sm:py-4 bg-white before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-12 sm:before:w-28 before:bg-gradient-to-r before:from-white before:to-transparent after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-12 sm:after:w-28 after:bg-gradient-to-l after:from-white after:to-transparent">
          <div className="flex w-max animate-marquee space-x-4 sm:space-x-6 items-center">
            {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((brand, idx) => (
              <div
                key={idx}
                className="w-[150px] h-[150px] bg-white border border-slate-200 rounded-xs hover:border-[#ffcc29] hover:shadow-xl transition-all duration-300 flex items-center justify-center group shrink-0 overflow-hidden"
              >
                {brand.image ? (
                  <img
                    src={brand.image}
                    alt={brand.name}
                    width={150}
                    height={150}
                    sizes="148px"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                ) : (
                  <span className="text-[#002b5c] font-heading font-bold text-xs sm:text-sm tracking-wider uppercase text-center">
                    {brand.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        {/* Modal Lightbox for Project Details */}
        {selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
            <div className="relative w-full max-w-4xl bg-[#002b5c] border border-white/20 rounded-xs shadow-2xl overflow-hidden text-white max-h-[90vh] flex flex-col md:flex-row">
              {/* Close Button */}
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/60 text-white hover:bg-[#ffcc29] hover:text-[#002b5c] transition-colors flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Modal Image */}
              <div className="md:w-1/2 h-64 md:h-auto relative bg-black/50">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  width={600}
                  height={450}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-[#ffcc29] text-[#002b5c] font-heading font-bold text-xs uppercase px-3 py-1">
                  {selectedProject.category}
                </div>
              </div>

              {/* Modal Details */}
              <div className="md:w-1/2 p-8 overflow-y-auto flex flex-col justify-between">
                <div>
                  <h3 className="font-heading text-3xl font-bold uppercase tracking-wide text-white mb-2">
                    {selectedProject.title}
                  </h3>
                  <div className="flex items-center gap-4 text-xs text-[#ffcc29] font-body mb-6">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />{" "}
                      {selectedProject.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" />{" "}
                      {selectedProject.area}
                    </span>
                  </div>

                  <p className="text-white/80 font-body text-sm leading-relaxed mb-6">
                    {selectedProject.description}
                  </p>

                  <div className="mb-6">
                    <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-[#ffcc29] mb-3">
                      Project Highlights
                    </h4>
                    <ul className="space-y-2">
                      {selectedProject.highlights.map((item, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-2 text-xs font-body text-white/90"
                        >
                          <CheckCircle2 className="w-4 h-4 text-[#ffcc29] shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/10 flex items-center justify-between">
                  <Link
                    to="/contact"
                    onClick={() => setSelectedProject(null)}
                    className="w-full text-center bg-[#ffcc29] text-[#002b5c] py-3 font-heading font-bold text-sm uppercase tracking-wider hover:bg-white transition-colors"
                  >
                    Request Similar Project
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
